import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from './Button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './Dialog';
import { Input } from './Input';
import { Label } from './Label';
import { Textarea } from './Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { useEvents } from '../contexts/EventsContext';
import { expertiseFields } from '../data/experts';
import type { EventFormat, NewEventInput } from '../types/event';
const emptyForm: NewEventInput = {
  name: '',
  description: '',
  date: '',
  location: '',
  format: 'In-person',
  category: expertiseFields[0],
  budget: 0
};
export function CreateEventDialog() {
  const {
    createEvent
  } = useEvents();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NewEventInput>(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const errors = {
    name: !form.name.trim(),
    date: !form.date,
    location: !form.location.trim()
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const update = <K extends keyof NewEventInput,>(key: K, value: NewEventInput[K]) => setForm(f => ({
    ...f,
    [key]: value
  }));
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setForm(emptyForm);
      setSubmitted(false);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (hasErrors) return;
    const id = createEvent(form);
    handleOpenChange(false);
    navigate(`/events/${id}`);
  };
  return <>
      <Button onClick={() => setOpen(true)} data-id="element-144" data-mp="2xm91pnbr2kg">
        <Plus data-id="element-145" data-mp="3k4zkefj6a3r7" /> Create event
      </Button>
      <Dialog open={open} onOpenChange={handleOpenChange} data-id="element-146" data-mp="3k6175h9jweor">
        <DialogContent className="sm:max-w-lg" data-id="element-147" data-mp="3p541o7rwn2ih">
          <form onSubmit={handleSubmit} noValidate data-id="element-148" data-mp="3p3swq4mq3ops">
            <DialogHeader data-id="element-149" data-mp="3607mmh4pt0fm">
              <DialogTitle data-id="element-150" data-mp="360r2ztepvgrj">Create event</DialogTitle>
              <DialogDescription data-id="element-151" data-mp="319iwb54ru9i2">Set up the event, then invite experts to take part.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-5" data-id="element-152" data-mp="2wb7kioux1ox7">
              <div className="grid gap-1.5" data-id="element-153" data-mp="2w7thrficbdp8">
                <Label htmlFor="ev-name" data-id="element-154" data-mp="2rbuqnk5mnhl4">Event name</Label>
                <Input id="ev-name" value={form.name} onChange={e => update('name', e.target.value)} aria-invalid={submitted && errors.name} placeholder="e.g. Smart Mobility Summit" data-id="element-155" data-mp="2r98jvmqk963y" />
                {submitted && errors.name && <p className="text-xs text-destructive" data-id="element-156" data-mp="3f0dy5xgurqc5">Event name is required.</p>}
              </div>
              <div className="grid gap-1.5" data-id="element-157" data-mp="3a1t3seoa80vm">
                <Label htmlFor="ev-desc" data-id="element-158" data-mp="3a11873uoq35c">Description</Label>
                <Textarea id="ev-desc" rows={3} value={form.description} onChange={e => update('description', e.target.value)} placeholder="What is the event about and who is the audience?" data-id="element-159" data-mp="23m0e73p7zv1e" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2" data-id="element-160" data-mp="e5ekey5lvqgf">
                <div className="grid gap-1.5" data-id="element-161" data-mp="j3g1xendvhps">
                  <Label htmlFor="ev-date" data-id="element-162" data-mp="j47z2twmpni6">Date</Label>
                  <Input id="ev-date" type="date" value={form.date} onChange={e => update('date', e.target.value)} aria-invalid={submitted && errors.date} data-id="element-163" data-mp="o2j9p6ur021g" />
                  {submitted && errors.date && <p className="text-xs text-destructive" data-id="element-164" data-mp="sv2o2619f17b">Pick a date.</p>}
                </div>
                <div className="grid gap-1.5" data-id="element-165" data-mp="xuoyk7ppe3cf">
                  <Label htmlFor="ev-format" data-id="element-166" data-mp="11swg34rzr8oo">Format</Label>
                  <Select value={form.format} onValueChange={v => update('format', v as EventFormat)} data-id="element-167" data-mp="16t0zb2kaahil">
                    <SelectTrigger id="ev-format" className="w-full" data-id="element-168" data-mp="1bs3w5zq41hfh"><SelectValue data-id="element-169" data-mp="1bqssdzwo09od" /></SelectTrigger>
                    <SelectContent data-id="element-170" data-mp="38kdtawkpr4zv">
                      <SelectItem value="In-person" data-id="element-171" data-mp="38lfdprnmd3u9">In-person</SelectItem>
                      <SelectItem value="Virtual" data-id="element-172" data-mp="3diyqen4o58k3">Virtual</SelectItem>
                      <SelectItem value="Hybrid" data-id="element-173" data-mp="3dlbiht454zav">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-1.5" data-id="element-174" data-mp="12vlgktoezof3">
                <Label htmlFor="ev-location" data-id="element-175" data-mp="1gvmhg94oivuk">Location</Label>
                <Input id="ev-location" value={form.location} onChange={e => update('location', e.target.value)} aria-invalid={submitted && errors.location} placeholder="Venue, Emirate" data-id="element-176" data-mp="1gv3a1mcnnpus" />
                {submitted && errors.location && <p className="text-xs text-destructive" data-id="element-177" data-mp="p2c8qxg47ayo">Location is required.</p>}
              </div>
              <div className="grid gap-4 sm:grid-cols-2" data-id="element-178" data-mp="k3sqp84n6jbl">
                <div className="grid gap-1.5" data-id="element-179" data-mp="xxtn3v84fu8s">
                  <Label htmlFor="ev-category" data-id="element-180" data-mp="t6bq1i97ydt3">Main field</Label>
                  <Select value={form.category} onValueChange={v => update('category', v)} data-id="element-181" data-mp="t50kbcw7qvzc">
                    <SelectTrigger id="ev-category" className="w-full" data-id="element-182" data-mp="5f66as05r1di"><SelectValue data-id="element-183" data-mp="1fnpa7bgjafy" /></SelectTrigger>
                    <SelectContent data-id="element-184" data-mp="1evtavdy7ap5">
                      {expertiseFields.map(f => <SelectItem key={f} value={f} data-id="element-185" data-mp="3nf0i8x59r39w">{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5" data-id="element-186" data-mp="3djgn4cyj48m7">
                  <Label htmlFor="ev-budget" data-id="element-187" data-mp="3dgkoemeuxfwp">Expert budget (AED)</Label>
                  <Input id="ev-budget" type="number" min={0} step={1000} value={form.budget || ''} onChange={e => update('budget', Number(e.target.value))} placeholder="50000" data-id="element-188" data-mp="3ii092dd4hkek" />
                </div>
              </div>
            </div>
            <DialogFooter data-id="element-189" data-mp="3j0n20x1kt6">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} data-id="element-190" data-mp="4ajrddbtq2k">Cancel</Button>
              <Button type="submit" data-id="element-191" data-mp="51khrhes7a20">Create event</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>;
}